import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from './newsintelligence/GlobalStateManagement/store';
import { NotificationProvider } from './layout/ReusableComponent/PopupNotification/PopupNotification';
import {
  APP_NAME,
  APPLE_TOUCH_ICON_SRC,
  FAVICON_ICO_SRC,
  FAVICON_PNG_SRC,
  FAVICON_SVG_SRC,
  MANIFEST_SRC,
} from './config/branding';

const root = ReactDOM.createRoot(document.getElementById('root'));

function applyBrandingFavicon() {
  const iconLinks = [
    { rel: 'icon', href: FAVICON_ICO_SRC, sizes: 'any' },
    { rel: 'icon', href: FAVICON_PNG_SRC, type: 'image/png', sizes: '32x32' },
    { rel: 'icon', href: FAVICON_SVG_SRC, type: 'image/svg+xml' },
    { rel: 'shortcut icon', href: FAVICON_ICO_SRC },
    { rel: 'apple-touch-icon', href: APPLE_TOUCH_ICON_SRC },
  ];

  document.querySelectorAll('link[rel*="icon"], link[rel="manifest"]').forEach((node) => {
    node.parentNode?.removeChild(node);
  });

  iconLinks.forEach(({ rel, href, type, sizes }) => {
    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (type) link.type = type;
    if (sizes) link.sizes = sizes;
    document.head.appendChild(link);
  });

  let manifest = document.querySelector('link[rel="manifest"]');
  if (!manifest) {
    manifest = document.createElement('link');
    manifest.rel = 'manifest';
    document.head.appendChild(manifest);
  }
  manifest.href = MANIFEST_SRC;

  if (document.title !== APP_NAME) {
    document.title = APP_NAME;
  }
}

applyBrandingFavicon();

/**
 * Mobile/narrow: floating overlay scrollbar (no reserved gutter).
 * Shows a thin thumb on top of content while scrolling, then auto-hides.
 */
function initOverlayScrollbar() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const mq = window.matchMedia('(max-width: 1080px)');
  let bar = null;
  let thumb = null;
  let hideTimer = null;
  let activeEl = null;

  const ensureBar = () => {
    if (bar || !document.body) return;
    bar = document.createElement('div');
    bar.id = 'od-overlay-scrollbar';
    bar.setAttribute('aria-hidden', 'true');
    thumb = document.createElement('div');
    thumb.id = 'od-overlay-scrollbar__thumb';
    bar.appendChild(thumb);
    document.body.appendChild(bar);
  };

  const hide = () => {
    document.documentElement.classList.remove('od-scroll-active');
    if (bar) bar.classList.remove('is-visible');
    activeEl = null;
  };

  const updateFromEl = (el) => {
    if (!mq.matches || !el) {
      hide();
      return;
    }
    const scrollEl = el === document.body ? document.documentElement : el;
    const scrollSize = scrollEl.scrollHeight - scrollEl.clientHeight;
    if (scrollSize <= 1) {
      hide();
      return;
    }

    ensureBar();
    if (!bar || !thumb) return;

    activeEl = scrollEl;
    const rect =
      scrollEl === document.documentElement
        ? { top: 0, right: window.innerWidth, height: window.innerHeight }
        : scrollEl.getBoundingClientRect();
    const track = Math.max(0, rect.height);
    const thumbH = Math.max(28, (scrollEl.clientHeight / scrollEl.scrollHeight) * track);
    const maxTop = Math.max(0, track - thumbH);
    const top = (scrollEl.scrollTop / scrollSize) * maxTop;

    bar.style.top = `${Math.max(0, rect.top)}px`;
    bar.style.height = `${track}px`;
    bar.style.right = `${Math.max(0, window.innerWidth - rect.right)}px`;
    thumb.style.height = `${thumbH}px`;
    thumb.style.transform = `translateY(${top}px)`;
    bar.classList.add('is-visible');
    document.documentElement.classList.add('od-scroll-active');

    window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(hide, 900);
  };

  const onScroll = (event) => {
    if (!mq.matches) return;
    const target = event.target;
    if (target === document || target === document.documentElement || target === document.body) {
      updateFromEl(document.documentElement);
      return;
    }
    if (!(target instanceof Element)) return;
    if (target.scrollHeight > target.clientHeight + 1) {
      updateFromEl(target);
    }
  };

  const onResize = () => {
    if (!mq.matches) {
      hide();
      return;
    }
    if (activeEl) updateFromEl(activeEl);
  };

  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', onResize, { passive: true });
  mq.addEventListener('change', onResize);
}

if (document.body) {
  initOverlayScrollbar();
} else {
  document.addEventListener('DOMContentLoaded', initOverlayScrollbar, { once: true });
}

// CRA dev overlay treats this benign browser warning as a fatal runtime error.
if (process.env.NODE_ENV === 'development') {
  window.addEventListener('error', (event) => {
    if (
      event.message === 'ResizeObserver loop completed with undelivered notifications.' ||
      event.message === 'ResizeObserver loop limit exceeded'
    ) {
      event.stopImmediatePropagation();
    }
  });
}

/** Overscroll / root fill — matches page shell, not the sticky app header bar. */
const OVERSCROLL_CHROME = {
  light: '#faf6f1',
  dark: '#050505',
};

/** Browser UI tint — aligned with visible app header (mobile chrome + shell header). */
const HEADER_CHROME = {
  light: { mobile: '#faf6f1', desktop: '#ffffff' },
  dark: { mobile: '#050505', desktop: '#050505' },
};

const MOBILE_CHROME_MQ = '(max-width: 960px)';

function setThemeColorMeta(content, media) {
  const meta = document.createElement('meta');
  meta.name = 'theme-color';
  meta.content = content;
  if (media) {
    meta.media = media;
  }
  document.head.appendChild(meta);
}

/** Keep browser chrome (status bar / address bar) in sync with app header colors. */
function applyBrowserChromeTheme(theme) {
  const resolved = theme === 'dark' ? 'dark' : 'light';
  const overscrollColor = OVERSCROLL_CHROME[resolved];
  const headerColors = HEADER_CHROME[resolved];

  // Explicit light/dark so browser chrome follows the toggle both ways.
  document.documentElement.style.colorScheme = resolved;

  // Force solid root fill so Safari/Chrome overscroll follows the toggle both ways.
  document.documentElement.style.backgroundColor = overscrollColor;
  if (document.body) {
    document.body.style.backgroundColor = overscrollColor;
  }

  // Safari often ignores in-place content updates; replace meta nodes.
  document.querySelectorAll('meta[name="theme-color"]').forEach((node) => {
    node.parentNode?.removeChild(node);
  });

  // Active manual theme (data-theme) — wins over prefers-color-scheme fallbacks below.
  setThemeColorMeta(headerColors.mobile, MOBILE_CHROME_MQ);
  setThemeColorMeta(headerColors.desktop, '(min-width: 961px)');

  // System light/dark fallbacks when OS preference differs from saved toggle.
  setThemeColorMeta(
    HEADER_CHROME.light.mobile,
    '(prefers-color-scheme: light) and (max-width: 960px)',
  );
  setThemeColorMeta(
    HEADER_CHROME.light.desktop,
    '(prefers-color-scheme: light) and (min-width: 961px)',
  );
  setThemeColorMeta(
    HEADER_CHROME.dark.mobile,
    '(prefers-color-scheme: dark) and (max-width: 960px)',
  );
  setThemeColorMeta(
    HEADER_CHROME.dark.desktop,
    '(prefers-color-scheme: dark) and (min-width: 961px)',
  );
}

// Check and set the initial theme based on localStorage
const savedTheme = localStorage.getItem('theme');
if (savedTheme === 'dark' || savedTheme === 'light') {
  document.documentElement.setAttribute('data-theme', savedTheme);
} else {
  document.documentElement.setAttribute('data-theme', 'light');
}

const syncChrome = () => {
  applyBrowserChromeTheme(document.documentElement.getAttribute('data-theme'));
};

if (document.body) {
  syncChrome();
} else {
  document.addEventListener('DOMContentLoaded', syncChrome, { once: true });
}

// Listen for theme changes
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.attributeName === 'data-theme') {
      applyBrowserChromeTheme(document.documentElement.getAttribute('data-theme'));
    }
  });
});

observer.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['data-theme'],
});

window.matchMedia(MOBILE_CHROME_MQ).addEventListener('change', () => {
  applyBrowserChromeTheme(document.documentElement.getAttribute('data-theme'));
});

root.render(
  // <React.StrictMode>
    <Provider store={store}>
    <NotificationProvider>
        <App />
    </NotificationProvider>
    </Provider>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();